import { useState, useEffect } from "react";
import { Users, Calendar, FileText, ChevronUp, RefreshCw } from "lucide-react";

interface StatsBannerProps {
  apiToken: string;
}

interface StatsData {
  totalPatients: number;
  patientsToday: number;
  pendingAppointments: number;
  pendingAppointmentsToday: number;
  clinicalRecordsToday: number;
  trends: {
    days: string[];
    patients: number[];
    appointments: number[];
    records: number[];
  };
}

export function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length === 0) return null;
  const width = 75;
  const height = 20;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const points = data
    .map((val, idx) => {
      const x = (idx / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * (height * 0.7) - height * 0.15;
      return `${x},${y}`;
    })
    .join(" ");

  const fillPoints = `${points} ${width},${height} 0,${height}`;

  return (
    <div className="relative group/spark inline-flex items-center">
      <svg width={width} height={height} className="overflow-visible select-none">
        <defs>
          <linearGradient id={`gradient-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0.0" />
          </linearGradient>
        </defs>
        <polygon points={fillPoints} fill={`url(#gradient-${color.replace("#", "")})`} />
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        {data.length > 0 && (() => {
          const lastVal = data[data.length - 1];
          const lastX = width;
          const lastY = height - ((lastVal - min) / range) * (height * 0.7) - height * 0.15;
          return (
            <>
              <circle cx={lastX} cy={lastY} r="2" fill={color} />
              <circle cx={lastX} cy={lastY} r="4" fill={color} className="animate-ping opacity-30" />
            </>
          );
        })()}
      </svg>
    </div>
  );
}

export default function StatsBanner({ apiToken }: StatsBannerProps) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const fetchStats = async () => {
    if (!apiToken) return;
    try {
      setLoading(true);
      setError(false);
      const res = await fetch("/api/stats", {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        setError(true);
      }
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Refresh stats periodically every 20 seconds
    const interval = setInterval(fetchStats, 20000);
    return () => clearInterval(interval);
  }, [apiToken]);

  if (loading && !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white border border-[#E2E8F0] p-3 rounded-lg flex items-center justify-between animate-pulse"
          >
            <div className="space-y-1.5">
              <div className="h-2.5 w-16 bg-slate-100 rounded"></div>
              <div className="h-4 w-10 bg-slate-200 rounded"></div>
              <div className="h-2 w-24 bg-slate-100 rounded"></div>
            </div>
            <div className="w-10 h-10 bg-slate-100 rounded-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-[#FFF5F5] border border-[#FEE2E2] p-2.5 rounded text-[#B91C1C] text-[11px] font-semibold mb-4 flex items-center justify-between">
        <span>Failed to sync database diagnostic analytics logs.</span>
        <button
          onClick={fetchStats}
          className="flex items-center gap-1 px-1.5 py-0.5 bg-white border border-[#FEE2E2] hover:bg-red-50 text-[10px] font-bold rounded cursor-pointer transition select-none"
        >
          <RefreshCw className="w-2.5 h-2.5 text-[#EF4444]" />
          Retry Sync
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 select-none animate-fade-in">
      {/* Stat 1: Registered Patients */}
      <div className="bg-white border border-[#E2E8F0] px-3.5 py-2 rounded-lg shadow-sm flex items-center justify-between hover:border-[#CBD5E1] transition group">
        <div className="space-y-0.5">
          <span className="text-[9px] text-[#64748B] font-bold uppercase tracking-wider block">
            Total Patients Enrolled
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-extrabold text-[#0F172A] tracking-tight">{stats.totalPatients}</span>
            <span className="text-[9px] text-[#10B981] font-bold flex items-center bg-[#F0FDF4] px-1 py-0.5 rounded border border-[#DCFCE7]">
              <ChevronUp className="w-2.5 h-2.5" />
              +{stats.patientsToday} today
            </span>
          </div>
          <span className="text-[8.5px] text-[#94A3B8] font-medium block leading-tight">
            Integrated clinic-wide active patient master index
          </span>
        </div>
        <div className="flex flex-col items-end justify-between h-full gap-1">
          <div className="bg-[#F8FAFC] text-[#0F172A] p-1.5 rounded border border-[#F1F5F9] group-hover:bg-[#F1F5F9] transition">
            <Users className="w-3 h-3" />
          </div>
          <div className="mt-0.5">
            <Sparkline data={stats.trends.patients} color="#0F172A" />
          </div>
        </div>
      </div>

      {/* Stat 2: Pending Appointments */}
      <div className="bg-white border border-[#E2E8F0] px-3.5 py-2 rounded-lg shadow-sm flex items-center justify-between hover:border-[#CBD5E1] transition group">
        <div className="space-y-0.5">
          <span className="text-[9px] text-[#64748B] font-bold uppercase tracking-wider block">
            Live Queue Appointments
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-extrabold text-[#4F46E5] tracking-tight">{stats.pendingAppointments}</span>
            <span className="text-[8.5px] text-indigo-700 font-bold bg-[#EEF2FF] px-1 py-0.5 rounded border border-[#E0E7FF]">
              {stats.pendingAppointmentsToday} scheduled today
            </span>
          </div>
          <span className="text-[8.5px] text-[#94A3B8] font-medium block leading-tight">
            Active physician consults and outpatient queue
          </span>
        </div>
        <div className="flex flex-col items-end justify-between h-full gap-1">
          <div className="bg-[#EEF2FF] text-[#4F46E5] p-1.5 rounded border border-[#E0E7FF] group-hover:bg-[#E0E7FF] transition">
            <Calendar className="w-3 h-3" />
          </div>
          <div className="mt-0.5">
            <Sparkline data={stats.trends.appointments} color="#4F46E5" />
          </div>
        </div>
      </div>

      {/* Stat 3: New Clinical Records */}
      <div className="bg-white border border-[#E2E8F0] px-3.5 py-2 rounded-lg shadow-sm flex items-center justify-between hover:border-[#CBD5E1] transition group">
        <div className="space-y-0.5">
          <span className="text-[9px] text-[#64748B] font-bold uppercase tracking-wider block">
            Clinical Records Logged
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-extrabold text-[#10B981] tracking-tight">
              {stats.clinicalRecordsToday}
            </span>
            <span className="text-[8.5px] text-emerald-700 font-bold bg-[#F0FDF4] px-1.5 py-0.5 rounded border border-[#DCFCE7] uppercase tracking-wide">
              EHR Synchronized
            </span>
          </div>
          <span className="text-[8.5px] text-[#94A3B8] font-medium block leading-tight">
            Dermatological clinical analysis and SOAP logs
          </span>
        </div>
        <div className="flex flex-col items-end justify-between h-full gap-1">
          <div className="bg-[#F0FDF4] text-[#10B981] p-1.5 rounded border border-[#DCFCE7] group-hover:bg-[#DCFCE7] transition">
            <FileText className="w-3 h-3" />
          </div>
          <div className="mt-0.5">
            <Sparkline data={stats.trends.records} color="#10B981" />
          </div>
        </div>
      </div>
    </div>
  );
}
